import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, PageLoader } from '@/components/common/Layout';
import { AssetMap } from '@/components/map/AssetMap';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ZONE_META } from '@/lib/format';
import { Trash2, Plus } from 'lucide-react';

const ZONE_TYPES = ['core', 'protection', 'catchment', 'downstream', 'access', 'slope', 'restricted'];

export default function AssetEditPage() {
  const { assetId } = useParams();
  const nav = useNavigate();
  const [asset, setAsset] = useState(null);
  const [zones, setZones] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, z] = await Promise.all([
      api.get(`/assets/${assetId}`).then(r => r.data),
      api.get('/zones', { params: { asset_id: assetId } }).then(r => r.data),
    ]);
    setAsset(a); setZones(z);
  };
  useEffect(() => { load(); }, [assetId]);

  const save = async () => {
    setBusy(true);
    try {
      const { id, org_id, created_at, updated_at, current_risk_level, operational_status, ...body } = asset;
      await api.patch(`/assets/${assetId}`, body);
      toast.success('Asset updated');
      nav(`/app/assets/${assetId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const addZone = async (payload) => {
    await api.post(`/zones/asset/${assetId}`, payload);
    load();
  };
  const removeZone = async (zid) => {
    if (!window.confirm('Delete this zone?')) return;
    await api.delete(`/zones/${zid}`);
    load();
  };

  if (!asset) return <div className="p-6"><PageLoader /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader title={`Edit — ${asset.name}`} subtitle="Update asset profile and monitoring zones" />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card className="p-0 overflow-hidden">
            <AssetMap asset={asset} zones={zones} height={480} />
          </Card>
        </div>
        <Card className="p-4">
          <div className="text-sm font-semibold">Asset info</div>
          <div className="mt-3 space-y-3">
            <div><Label>Name</Label><Input value={asset.name} onChange={e => setAsset({ ...asset, name: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea rows={2} value={asset.description} onChange={e => setAsset({ ...asset, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lat</Label><Input type="number" step="0.0001" value={asset.lat} onChange={e => setAsset({ ...asset, lat: parseFloat(e.target.value) })} /></div>
              <div><Label>Lng</Label><Input type="number" step="0.0001" value={asset.lng} onChange={e => setAsset({ ...asset, lng: parseFloat(e.target.value) })} /></div>
            </div>
            <div><Label>Location name</Label><Input value={asset.location_name} onChange={e => setAsset({ ...asset, location_name: e.target.value })} /></div>
            <div><Label>Criticality</Label>
              <Select value={asset.criticality} onValueChange={v => setAsset({ ...asset, criticality: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem><SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem><SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={save} disabled={busy} className="w-full mt-4" data-testid="asset-save-btn">{busy ? 'Saving…' : 'Save asset'}</Button>
        </Card>
      </div>

      <Card className="p-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">Monitoring zones</div>
          <NewZoneDialog onCreate={addZone} asset={asset} />
        </div>
        <div className="divide-y divide-border">
          {zones.length === 0 ? <div className="text-sm text-muted-foreground py-4">No zones yet</div> :
            zones.map(z => (
              <div key={z.id} className="flex items-center gap-3 py-2">
                <span className="h-3 w-3 rounded-full" style={{ background: ZONE_META[z.zone_type]?.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{z.name}</div>
                  <div className="text-xs text-muted-foreground">{ZONE_META[z.zone_type]?.label} · {z.risk_category}</div>
                </div>
                <Badge variant="outline" className="text-[10px] font-mono">threshold {z.alert_threshold_area_pct}%</Badge>
                <Button variant="ghost" size="sm" onClick={() => removeZone(z.id)}><Trash2 size={14} className="text-red-500" /></Button>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}

function NewZoneDialog({ onCreate, asset }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', zone_type: 'protection', description: '', risk_category: 'Moderate', alert_threshold_area_pct: 0.5, dx: 0.02, dy: 0.02, off_lat: 0, off_lng: 0 });
  const submit = async () => {
    const lat = asset.lat + f.off_lat, lng = asset.lng + f.off_lng;
    const payload = {
      name: f.name || `${f.zone_type} zone`,
      zone_type: f.zone_type,
      description: f.description,
      risk_category: f.risk_category,
      alert_threshold_area_pct: parseFloat(f.alert_threshold_area_pct),
      min_change_area_px: 300,
      boundary: { coords: [
        [lat - f.dy, lng - f.dx], [lat - f.dy, lng + f.dx],
        [lat + f.dy, lng + f.dx], [lat + f.dy, lng - f.dx], [lat - f.dy, lng - f.dx],
      ] },
    };
    await onCreate(payload);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-zone-btn"><Plus size={14} className="mr-1" /> Add zone</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add monitoring zone</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Name</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Type</Label>
            <Select value={f.zone_type} onValueChange={v => setF({ ...f, zone_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ZONE_TYPES.map(t => <SelectItem key={t} value={t}>{ZONE_META[t]?.label || t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Risk category</Label>
            <Select value={f.risk_category} onValueChange={v => setF({ ...f, risk_category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Low">Low</SelectItem><SelectItem value="Moderate">Moderate</SelectItem>
                <SelectItem value="High">High</SelectItem><SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Threshold (%)</Label><Input type="number" step="0.1" value={f.alert_threshold_area_pct} onChange={e => setF({ ...f, alert_threshold_area_pct: e.target.value })} /></div>
            <div><Label>Size (deg)</Label><Input type="number" step="0.005" value={f.dx} onChange={e => setF({ ...f, dx: parseFloat(e.target.value), dy: parseFloat(e.target.value) })} /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
